/**
 * Versioned TEMPORAL anchor for pinned review comments (UX Pack F5a).
 *
 * Deliberately NOT editing/element-target-signature.js: that format finds the
 * CORRESPONDING descendant across sibling repeat units at one moment in time,
 * and its ordinal tiebreak among identical siblings is exactly wrong for
 * tracking ONE element through edits — after a reorder it would happily
 * resolve to whichever sibling now occupies the old position. A comment
 * anchor must instead follow the element's CONTENT through time and treat
 * position as the LAST tiebreaker only.
 *
 * Anchor v1 fields, most→least identifying:
 *   eid          — the original data-pb-eid (fast path; volatile by design)
 *   tag          — element tag name
 *   attrs        — allowlisted stable authored attributes (id, href, src, alt)
 *   textDigest   — normalized text excerpt (whitespace-collapsed, lowercased,
 *                  first 120 chars) — content identity through restamps
 *   classes      — authored classes (builder-injected pb- noise filtered)
 *   path         — ancestor structural path with per-level ordinals
 *
 * Resolution contract: a UNIQUE confident match or null. AMBIGUOUS IS
 * ORPHANED, NEVER GUESSED — two candidates with the same content and no
 * ordinal agreement yield null, and the UI shows a disclosed orphan state.
 *
 * Pure module: DOMParser comes from the environment (browser, or jsdom in
 * tests); no imports.
 */

export const COMMENT_ANCHOR_VERSION = 1;
const TEXT_DIGEST_LENGTH = 120;
const STABLE_ATTRS = ['id', 'href', 'src', 'alt'];
const MAX_ANCHOR_JSON_BYTES = 4096;

function normalizeText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, TEXT_DIGEST_LENGTH);
}

function authoredClasses(el) {
  return Array.from(el.classList || []).filter((cls) => !cls.startsWith('pb-'));
}

function stableAttrs(el) {
  const out = {};
  for (const name of STABLE_ATTRS) {
    const value = el.getAttribute?.(name);
    if (typeof value === 'string' && value) out[name] = value.slice(0, 300);
  }
  return out;
}

function parseSection(sectionHtml) {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<div>${sectionHtml || ''}</div>`, 'text/html');
  return doc.body?.firstElementChild || null;
}

/** Ordinal of `el` among siblings sharing its tag (and how many there are). */
function ordinalAmongSiblings(el) {
  const parent = el.parentElement;
  if (!parent) return { ordinal: 0, of: 1 };
  const sameTag = Array.from(parent.children).filter((sib) => sib.tagName === el.tagName);
  return { ordinal: Math.max(0, sameTag.indexOf(el)), of: sameTag.length };
}

function structuralPath(root, el) {
  const path = [];
  let node = el;
  while (node && node !== root && node.parentElement) {
    const { ordinal, of } = ordinalAmongSiblings(node);
    path.unshift({ tag: node.tagName.toLowerCase(), ordinal, of });
    node = node.parentElement;
  }
  return path;
}

/**
 * Build a v1 anchor for the element carrying `eid` inside `sectionHtml`.
 * Returns null when the element is absent or the environment lacks DOMParser.
 */
export function computeCommentAnchor(sectionHtml, eid) {
  if (!eid) return null;
  const root = parseSection(sectionHtml);
  if (!root) return null;
  const matches = Array.from(root.querySelectorAll('[data-pb-eid]')).filter(
    (element) => element.getAttribute('data-pb-eid') === eid
  );
  if (matches.length !== 1) return null;
  return commentAnchorForElement(root, matches[0]);
}

/** Describe an already-resolved occurrence; an eid alone cannot select a duplicate. */
export function commentAnchorForElement(root, el) {
  if (!root?.contains(el)) return null;
  const anchor = {
    version: COMMENT_ANCHOR_VERSION,
    eid: el.getAttribute('data-pb-eid'),
    tag: el.tagName.toLowerCase(),
    attrs: stableAttrs(el),
    textDigest: normalizeText(el.textContent),
    classes: authoredClasses(el),
    path: structuralPath(root, el)
  };
  return JSON.stringify(anchor).length <= MAX_ANCHOR_JSON_BYTES ? anchor : null;
}

/** Parse + shape-check a stored anchor_json string. Unknown versions → null. */
export function parseCommentAnchor(anchorJson) {
  if (!anchorJson) return null;
  let parsed;
  try {
    parsed = typeof anchorJson === 'string' ? JSON.parse(anchorJson) : anchorJson;
  } catch {
    return null;
  }
  if (parsed?.version !== COMMENT_ANCHOR_VERSION) return null;
  if (typeof parsed.tag !== 'string' || !parsed.tag) return null;
  return parsed;
}

function contentScore(el, anchor) {
  let score = 0;
  const attrs = stableAttrs(el);
  if (anchor.attrs?.id && attrs.id === anchor.attrs.id) score += 8;
  for (const name of ['href', 'src', 'alt']) {
    if (anchor.attrs?.[name] && attrs[name] === anchor.attrs[name]) score += 3;
  }
  const digest = normalizeText(el.textContent);
  if (anchor.textDigest && digest === anchor.textDigest) score += 5;
  const classes = authoredClasses(el);
  const anchorClasses = Array.isArray(anchor.classes) ? anchor.classes : [];
  if (anchorClasses.length && anchorClasses.every((cls) => classes.includes(cls))) score += 1;
  return score;
}

/** A candidate is only confident when something beyond tag+classes matched. */
const MIN_CONFIDENT_SCORE = 3;

function pathOrdinal(root, el) {
  const path = structuralPath(root, el);
  return path.length ? path[path.length - 1].ordinal : 0;
}

/**
 * Resolve an anchor against the CURRENT section html of the version being
 * viewed. Returns {eid, tier} or null (orphaned):
 *   tier 'eid'       — the original eid still exists and its content agrees
 *   tier 'content'   — unique confident content match (survives restamps)
 *   tier 'structure' — content tie broken by the stored ordinal (LAST resort)
 */
export function resolveCommentAnchor(sectionHtml, anchorJson) {
  const anchor = parseCommentAnchor(anchorJson);
  if (!anchor) return null;
  const root = parseSection(sectionHtml);
  if (!root) return null;

  // Fast path: same eid, same tag, and the content has not been swapped out
  // from under it (a restamp can hand the eid to a DIFFERENT element).
  const byEid = anchor.eid
    ? root.querySelector(`[data-pb-eid="${CSS?.escape ? CSS.escape(anchor.eid) : anchor.eid}"]`)
    : null;
  if (byEid && byEid.tagName.toLowerCase() === anchor.tag) {
    const score = contentScore(byEid, anchor);
    if (score >= MIN_CONFIDENT_SCORE || (!anchor.textDigest && score >= 1)) {
      return { eid: anchor.eid, tier: 'eid' };
    }
  }

  // Content pass: every same-tag element WITH an identity, scored.
  const candidates = Array.from(root.querySelectorAll(`${anchor.tag}[data-pb-eid]`))
    .map((el) => ({ el, score: contentScore(el, anchor) }))
    .filter((entry) => entry.score >= MIN_CONFIDENT_SCORE);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.score - a.score);
  const top = candidates.filter((entry) => entry.score === candidates[0].score);
  if (top.length === 1) {
    return { eid: top[0].el.getAttribute('data-pb-eid'), tier: 'content' };
  }

  // FINAL tiebreak only: among content-tied candidates, exactly one whose
  // current ordinal equals the stored one. Anything else is orphaned.
  const storedOrdinal = Array.isArray(anchor.path)
    ? (anchor.path[anchor.path.length - 1]?.ordinal ?? null)
    : null;
  if (storedOrdinal == null) return null;
  const byOrdinal = top.filter((entry) => pathOrdinal(root, entry.el) === storedOrdinal);
  if (byOrdinal.length === 1) {
    return { eid: byOrdinal[0].el.getAttribute('data-pb-eid'), tier: 'structure' };
  }
  return null;
}
